import React, { Component, ErrorInfo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../styles/theme';
import { captureError } from '../services/analyticsService';
import i18n from '../i18n';

interface Props {
  children: React.ReactNode;
  onGoHome?: () => void;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureError(error, {
      componentStack: errorInfo.componentStack || '',
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  handleGoHome = () => {
    if (this.props.onGoHome) {
      this.props.onGoHome();
      this.setState({ hasError: false });
    } else if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  render() {
    if (this.state.hasError) {
      // A class component has no hook; i18n.t at render time follows the language.
      const t = i18n.t.bind(i18n);
      return (
        <View style={styles.container}>
          <Text style={styles.emoji}>:(</Text>
          <Text style={styles.title}>{t('common.somethingWentWrong')}</Text>
          <Text style={styles.message}>{t('errorBoundary.message')}</Text>
          <TouchableOpacity style={styles.primaryButton} onPress={this.handleRetry}>
            <Text style={styles.primaryButtonText}>{t('errorBoundary.retry')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.secondaryButton} onPress={this.handleGoHome}>
            <Text style={styles.secondaryButtonText}>{t('errorBoundary.goHome')}</Text>
          </TouchableOpacity>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Paprika.background,
    paddingHorizontal: PaprikaSpacing.xl,
  },
  emoji: {
    fontSize: 48,
    marginBottom: PaprikaSpacing.md,
    color: Paprika.textMuted,
  },
  title: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.sm,
    textAlign: 'center',
  },
  message: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    textAlign: 'center',
    marginBottom: PaprikaSpacing.lg,
  },
  primaryButton: {
    backgroundColor: Paprika.accent,
    paddingVertical: PaprikaSpacing.sm + 4,
    paddingHorizontal: PaprikaSpacing.xl,
    borderRadius: PaprikaRadius.md,
    marginBottom: PaprikaSpacing.sm,
    minWidth: 200,
    alignItems: 'center',
  },
  primaryButtonText: {
    ...PaprikaTypography.labelMedium,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  secondaryButton: {
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.xl,
    minWidth: 200,
    alignItems: 'center',
  },
  secondaryButtonText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
  },
});
